// Vínculo projeto↔insumo (`project_supplies`) — a parte central e
// obrigatoriamente testável da Etapa 10 (spec S02): gravar
// `unit_price_snapshot` com o preço do catálogo NO MOMENTO do vínculo, sem
// nunca reescrevê-lo depois. É esse valor (não `supplies.unit_price`) que
// protege o histórico de custo de projetos antigos quando o preço do
// insumo muda no catálogo.
import { eq } from 'drizzle-orm';
import { supplies, projectSupplies, type ProjectSupply } from '../../db/schema';
import { insertRecord } from '../../db/writes';
import type { AppDatabase } from '../../db/outbox';

// A mensagem desta exceção é só para log/depuração (nunca exibida
// diretamente à usuária — regra invólavel #1, toda string de UI via i18n).
// `supplyId` fica disponível como dado estruturado para quem capturar o
// erro montar a mensagem final via `t()` no ponto de exibição (ver
// src/ui/projects/ProjectSuppliesSection.tsx).
export class SupplyNotFoundError extends Error {
  readonly supplyId: string;

  constructor(supplyId: string) {
    super(`Insumo não encontrado: ${supplyId}`);
    this.name = 'SupplyNotFoundError';
    this.supplyId = supplyId;
  }
}

/**
 * Vincula um insumo já existente do catálogo a um projeto. Lê o preço
 * ATUAL do insumo e grava esse valor como `unit_price_snapshot` do vínculo
 * — a usuária nunca redigita o preço ao reusar um insumo em outro projeto
 * (critério de aceite da S02).
 */
export async function linkSupplyToProject(
  db: AppDatabase,
  params: { projectId: string; supplyId: string; quantity: number }
): Promise<ProjectSupply> {
  const supply = await db.select().from(supplies).where(eq(supplies.id, params.supplyId)).get();
  if (!supply) {
    throw new SupplyNotFoundError(params.supplyId);
  }

  return insertRecord(db, projectSupplies, {
    projectId: params.projectId,
    supplyId: params.supplyId,
    quantity: params.quantity,
    unitPriceSnapshot: supply.unitPrice,
  });
}

/**
 * Fluxo "criar insumo inline" (dentro da tela de projeto, ao adicionar um
 * insumo que ainda não existe no catálogo): cria a linha em `supplies`
 * (que passa a existir no catálogo pessoal, reutilizável por qualquer
 * projeto a partir de agora) e, na sequência, vincula ao projeto atual —
 * reaproveitando `linkSupplyToProject` para o snapshot, em vez de duplicar
 * a lógica.
 */
export async function createSupplyAndLinkToProject(
  db: AppDatabase,
  params: {
    userId: string;
    projectId: string;
    quantity: number;
    type: string;
    name: string;
    unit: string;
    unitPrice: number;
  }
): Promise<ProjectSupply> {
  // Atribuído a uma variável antes de passar para insertRecord — um
  // literal de objeto "fresco" direto na chamada confunde a checagem de
  // excesso de propriedades do TS contra o generic com union de tabelas
  // (WritableTable), mesma pegadinha documentada em src/db/writes.test.ts.
  const newSupplyData = {
    userId: params.userId,
    type: params.type,
    name: params.name,
    unit: params.unit,
    unitPrice: params.unitPrice,
  };
  const supply = await insertRecord(db, supplies, newSupplyData);

  return linkSupplyToProject(db, { projectId: params.projectId, supplyId: supply.id, quantity: params.quantity });
}
